import { queryKeys, useHttpClient, useActiveOrganizationId } from '@repo/api-client/react';
import {
  ClientSchema,
  type Client,
  type CreateClientRequest,
} from '@repo/api-client/dtos';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postJSON } from '../internal/http';

/**
 * Create a client under the active org.
 *
 * The request body matches the server's `CreateClientRequest`; we do
 * not lift `organizationId` into the body since the route already
 * scopes by `:orgId` in the URL. The mutation includes a fresh
 * `Idempotency-Key` so retries (e.g. background sync after a flaky
 * connection) don't duplicate the row.
 */
export function useCreateClient() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const orgId = useActiveOrganizationId();

  const mutation = useMutation({
    mutationFn: (body: CreateClientRequest) => {
      if (!orgId) throw new Error('No active organization');
      return postJSON(http, `/orgs/${orgId}/clients`, body, ClientSchema);
    },
    onSuccess: (client) => {
      queryClient.setQueryData(
        queryKeys.clients.detail(client.orgId, client.id),
        client,
      );
      // The list cache is an `InfiniteData<{ items, nextCursor? }>`
      // shape since the B1 (2026-05-09) migration to
      // `useInfiniteQuery`. Append the new row to the FIRST page so
      // it lands at the top of the list (which is sorted by id
      // desc); the subsequent invalidate refetches the canonical
      // ordering from the server.
      type ClientsPage = { items: Client[]; nextCursor?: string };
      type ClientsCache = { pages: ClientsPage[]; pageParams: unknown[] };
      queryClient.setQueryData<ClientsCache>(
        queryKeys.clients.all(client.orgId),
        (cache) => {
          if (!cache || !cache.pages || cache.pages.length === 0) {
            return { pages: [{ items: [client] }], pageParams: [undefined] };
          }
          const [first, ...rest] = cache.pages;
          return {
            ...cache,
            pages: [
              { ...first!, items: [client, ...first!.items] },
              ...rest,
            ],
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all(client.orgId) });
    },
  });

  return {
    createClient: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
